import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { callQuery, Lab, LABS_BY_CSN, LLMVisitFeedback, Medication } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";
import { parseLabsDataForModal } from "../../DashboardDataParser";
import { LabsModal } from "../../Labs";
import { EvidenceBlock } from "../common/EvidenceBlock";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { ScanMulti } from "../common/models";
import { MedsModal } from "../../Meds";
import { OrdersModal } from "../../Orders";
import { callFhirMedications } from "../../../../amplify/appsync/fetch-sync-fhir";
import { useNavigate } from "react-router";

export const SectionRiskDetails = (props: {
    data: any,
    title: string,
    scanMulti?: ScanMulti,
    color?: string,
    patientVisit?: PatientVisit,
    feedbacks: LLMVisitFeedback[],
    setFeedbacks: any,
}) => {
    const navigate = useNavigate();

    const [showLabsModal, setShowLabsModal] = useState<any>(false);
    const [showOrdersModal, setShowOrdersModal] = useState<any>(false);

    const [labsData, setLabsData] = useState<Lab[][]>([]);

    useEffect(() => {
        if (!showLabsModal || !props.patientVisit) {
            return;
        }

        callQuery({
            operation: LABS_BY_CSN,
            csn: props.patientVisit.csn,
        }).then((labsData) => {
            const labs: Lab[] = labsData;
            setLabsData(parseLabsDataForModal(labs));
        });
    }, [showLabsModal]);

    if (!props.data) {
        return <></>;
    }

    // Add this helper function inside your file
    const getRiskLevelStyles = (riskLevel: string) => {
        switch (riskLevel) {
            case "Low":
                return { color: "green", icon: faArrowDown };
            case "Moderate":
                return { color: "orange", icon: null };
            case "High":
                return { color: "red", icon: faArrowUp };
            case "Very High":
                return { color: "darkred", icon: faArrowUp };
            default:
                return { color: "gray", icon: null };
        }
    };

    return (
        <Card className="mt-2">
            <Card.Body>
                <Row className="mb-2">
                    <Col md={9} className={props.color ?? ""}>
                        <h5>{props.title}</h5>
                    </Col>
                    <Col md={3} className="text-right">
                        <LLMDashboardFeedbackBar patientVisit={props.patientVisit} feedbackTarget={`risk-${props.title}`} feedbacks={props.feedbacks} setFeedbacks={props.setFeedbacks} />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Row className="h-100">
                            <Col className="risk-prob-section ms-2">
                                <Row>
                                    <Col>
                                        <strong>Calibrated Probability</strong>
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col><h4>{props.data.probability ?? "N/A"}</h4></Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row className="h-100">
                            <Col className="risk-prob-section">
                                <Row>
                                    <Col>
                                        <strong>Percentile (Risk Basis)</strong>
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col><h4>{props.data.probability_percentile ?? "N/A"}</h4></Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row className="h-100">
                            <Col className="risk-prob-section mr-2">
                                <Row>
                                    <Col>
                                        <strong>Risk Level</strong>
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col>
                                        {(() => {
                                            const riskLevel = props.data.risk_level ?? "N/A";
                                            const { color, icon } = getRiskLevelStyles(riskLevel);
                                            return (
                                                <h4 style={{ color: color, display: "flex", alignItems: "center" }}>
                                                    {riskLevel}
                                                    {icon && <FontAwesomeIcon icon={icon} className="ms-2" />}
                                                </h4>
                                            );
                                        })()}
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col className="description-heading">
                        Risk Modifiability
                    </Col>
                </Row>
                <Row>
                    <Col className="description-text">
                        Level: <strong>{props.data.risk_modifiable}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col className="description-text">
                        <LLMDashboardMarkdownRenderer content={props.data.risk_modifiable_justification} scanMulti={props.scanMulti} />
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col className="description-heading">
                        Key Risk Factors
                    </Col>
                </Row>
                {
                    props.data.key_risk_factors && props.data.key_risk_factors.map((factor: any) => (
                        <Card className="mb-2">
                            <Card.Body>
                                <Row>
                                    <Col md={8}>
                                        <h6>{factor["risk_factor"]}</h6>
                                    </Col>
                                    <Col md={4} className="text-right">
                                        <small>{factor["importance"] >= 0 ? <Badge bg="warning">Increases Risk</Badge> : <Badge bg="secondary">Decreases Risk</Badge>}</small>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="risk-factor-details">
                                        <strong>Clinical Context</strong>: <LLMDashboardMarkdownRenderer content={factor["clinical_context"]} scanMulti={props.scanMulti} />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="risk-factor-details">
                                        <strong>Modifiable</strong>:  {JSON.stringify(factor["modifiable"])}
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="risk-factor-details">
                                        <strong>Intervention</strong>: <LLMDashboardMarkdownRenderer content={factor["intervention"]} scanMulti={props.scanMulti} />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="risk-factor-details">
                                        {
                                            factor && factor.risk_key && (factor.risk_key.startsWith("Lab_") || factor.risk_key.startsWith("VS_") || factor.risk_key.startsWith("VSA_") || factor.risk_key.startsWith("Nonmed_") || factor.risk_key.startsWith("Med_")) && (
                                                <span
                                                    className="citation-bubble citation-bubble-light"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e: any) => {
                                                        if (factor.risk_key.startsWith("Lab_")) {
                                                            setShowLabsModal(true);
                                                        } else if (factor.risk_key.startsWith("Med_") || factor.risk_key.startsWith("Nonmed_")) {
                                                            setShowOrdersModal(true);
                                                        } else if (props.patientVisit && factor.risk_key.startsWith("VS_") || factor.risk_key.startsWith("VSA_")) {
                                                            navigate(`/dashboard/${props.patientVisit?.internal_csn}/overview`);
                                                        }
                                                    }}
                                                >
                                                    View Source
                                                </span>
                                            )
                                        }
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))
                }

                <EvidenceBlock evidence={props.data.evidence.map((evi: any) => {
                    return {
                        ...evi,
                        source: evi.citation,
                        details: evi.content,
                    }
                })} scanMulti={props.scanMulti} />

                <LabsModal showModal={showLabsModal} setShowModal={setShowLabsModal} labs={labsData} patientVisit={props.patientVisit} addAnnotationToPlots={() => {}} highlightedLabTime={null} />
                {
                    props.patientVisit && (
                        <OrdersModal showModal={showOrdersModal} setShowModal={setShowOrdersModal} patientVisit={props.patientVisit} addAnnotationToPlots={() => {}} />
                    )
                }
            </Card.Body>
        </Card>
    );
};
